// Protocolo do Hábito — Estágios 0 e 1: Qualificação e Preparação.
//
// Esta é a primeira coisa que um usuário novo vê depois de criar a conta, e é a
// tela mais decisiva do produto inteiro. Duas regras a governam:
//
//   · O risco que mata o Estágio 2 é "um onboarding longo que a pessoa abandona
//     no meio" — o apego ao que se construiu desaparece quando a tarefa não é
//     concluída. Então: uma pergunta por tela, quase tudo em um toque.
//   · A técnica central tem efeito ZERO em quem não quer mudar. Por isso a
//     triagem vem ANTES do contrato, e quem ainda não decidiu segue outro caminho.
//
// Deliberadamente NÃO se pergunta avatar, @username nem bio aqui. Identidade
// antes da primeira página lida é exatamente a inversão que o protocolo combate.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AuthSession, BookV2 } from '../../types';
import { BookCover, Sheet } from '../common';
import { Icon } from '../icons';
import { habitoApi, EstadoHabito, Sugestoes } from './api';
import { EscolherLivro } from './livro';

type Passo = 'abertura' | 'decisao' | 'publico' | 'interesse' | 'livro' | 'contrato' | 'ensaio' | 'selado' | 'convite';

const serif = 'Instrument Serif, Georgia, serif';

const eyebrow: React.CSSProperties = {
  fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase',
  color: 'var(--ink-3)', fontWeight: 600,
};

const titulo: React.CSSProperties = {
  fontFamily: serif, fontSize: 33, lineHeight: 1.08, color: 'var(--ink)',
  margin: '10px 0 12px', letterSpacing: '-0.01em',
};

const corpo: React.CSSProperties = {
  fontSize: 15.5, lineHeight: 1.6, color: 'var(--ink-2)', margin: 0,
};

// A capa gerada precisa continuar dentro da paleta da casa: só a família quente
// (vermelho → âmbar → ocre). Espalhar por 300 graus jogava verde e roxo no meio
// de uma tela que é toda papel e brasa. Precisa ser igual à do servidor.
function hueDe(t: string) { return 18 + (t.length * 13) % 72; }

/* ── Um cartão de escolha. Grande, tocável, sem checkbox. ─────────────── */
const Escolha: React.FC<{
  titulo: string; legenda?: string; selecionado?: boolean; onClick: () => void; sufixo?: React.ReactNode;
}> = ({ titulo: t, legenda, selecionado, onClick, sufixo }) => (
  <button
    onClick={onClick}
    style={{
      display: 'block', width: '100%', textAlign: 'left', padding: '15px 17px',
      borderRadius: 18, marginBottom: 10, cursor: 'pointer',
      background: selecionado ? 'var(--ember-soft)' : 'var(--paper-2)',
      border: `1px solid ${selecionado ? 'var(--ember)' : 'var(--line)'}`,
      transition: 'background .18s, border-color .18s, transform .18s',
      transform: selecionado ? 'translateY(-1px)' : 'none',
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: serif, fontSize: 20, lineHeight: 1.2, color: 'var(--ink)' }}>{t}</div>
        {legenda && <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 3, lineHeight: 1.45 }}>{legenda}</div>}
      </div>
      {sufixo}
    </div>
  </button>
);

/* ── O ensaio. Ensaiar UMA vez eleva o efeito de 0,33 para 0,50; ensaiar
     mais não adiciona nada. É a melhor relação custo-benefício disponível —
     e é ele, não o formulário, que sela o contrato. ──────────────────── */
const Ensaio: React.FC<{ momento: string; onde: string; pagina: number; onPronto: () => void }> = ({ momento, onde, pagina, onPronto }) => {
  const cenas = useMemo(() => [
    { t: momento, s: 'Olhe em volta. Que horas são, que barulho tem, quem está por perto.' },
    { t: `Você está ${onde}.`, s: 'Veja o lugar exato. A luz, a cadeira, onde suas mãos ficam.' },
    { t: 'Você pega o livro.', s: 'Sinta o peso dele. Ouça o som da capa abrindo.' },
    { t: `Abre na página ${pagina}.`, s: 'Lê a primeira linha. Uma página basta. Você fecha o livro e se sente bem.' },
  ], [momento, onde, pagina]);

  const [i, setI] = useState(0);
  const ultima = i >= cenas.length - 1;

  // SEM relógio. A instrução manda fechar os olhos entre uma cena e outra, e
  // avanço automático torna isso impossível: de olhos fechados a pessoa não vê
  // a cena trocar. Ou ela perde cenas, ou fica vigiando o app — que é o oposto
  // do exercício.
  //
  // O ciclo agora é dela: lê, fecha os olhos, imagina, abre, toca. E não existe
  // ritmo certo — uma cena pode levar 3 segundos e outra 20.
  function avancar() {
    setI((v) => Math.min(v + 1, cenas.length - 1));
  }

  const cena = cenas[i];

  // Sem onClick no container: quem esta de olhos fechados encosta na tela sem
  // querer, e avancar a cena por acidente estraga o exercicio. So o botao avanca.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', paddingTop: 8 }}>
      {/* respiração */}
      <div style={{ position: 'relative', width: 132, height: 132, marginBottom: 30 }}>
        <div style={{
          position: 'absolute', inset: 0, borderRadius: '50%',
          border: '1px solid var(--ember)', opacity: 0.28,
          animation: 'habRespira 6.2s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', inset: 22, borderRadius: '50%',
          background: 'radial-gradient(circle, var(--ember-soft), transparent 72%)',
          animation: 'habRespira 6.2s ease-in-out infinite reverse',
        }} />
        <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
          <div className="mono" style={{ fontSize: 11, color: 'var(--ember)', letterSpacing: '0.14em' }}>
            {i + 1}/{cenas.length}
          </div>
        </div>
      </div>

      <div key={i} style={{ animation: 'e2FadeUp .5s ease both', minHeight: 132 }}>
        <div style={{ fontFamily: serif, fontSize: 28, lineHeight: 1.15, color: 'var(--ink)', marginBottom: 10 }}>{cena.t}</div>
        <div style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--ink-2)', maxWidth: 320 }}>{cena.s}</div>
      </div>

      <button
        onClick={() => { if (ultima) onPronto(); else avancar(); }}
        style={{
          marginTop: 26, padding: '15px 30px', borderRadius: 999, border: 'none',
          background: 'var(--ember)', color: '#fff',
          fontSize: 15.5, fontWeight: 600, cursor: 'pointer',
          boxShadow: '0 12px 28px -12px var(--ember)',
        }}
      >{ultima ? 'Terminei o ensaio' : 'Próxima cena'}</button>

      {/* A saida so aparece a partir da segunda cena: quem chegou ate aqui
          ja investiu o preparo inteiro, e prender nao ajuda — o risco
          nomeado deste fluxo e o abandono no meio, sem contrato nenhum.
          Some na ultima, onde o botao principal ja conclui. */}
      {!ultima && i >= 1 && (
        <button onClick={(ev) => { ev.stopPropagation(); onPronto(); }} style={{
          background: 'none', border: 'none', color: 'var(--ink-4)',
          fontSize: 13, padding: '14px 8px 0', cursor: 'pointer',
          animation: 'e2FadeUp .4s ease both',
        }}>
          pular o ensaio
        </button>
      )}
    </div>
  );
};


/* ── A abertura ────────────────────────────────────────────────────────────
   Premium se sente, nao se argumenta — e um produto que vai cobrar assinatura
   e julgado nos primeiros segundos, antes de qualquer feature.

   Mas o momento de marca NAO E UM PASSO. Se tivesse botao, voltaria a custar um
   toque e um ponto de desistencia, que foi exatamente o que a tela de abertura
   anterior cobrava. Aqui ela roda sozinha em ~2,4 s e dissolve na primeira
   pergunta; tocar pula na hora.

   A metafora ja esta no nome: Forjamente e forja. Faisca -> brasa -> a marca.
   Nenhum superlativo: marca cara nunca diz que e cara, e "o melhor app do
   mercado" e a frase que faz o usuario exigente desconfiar de todo o resto. */
export const Abertura: React.FC<{ nome?: string; onFim: () => void }> = ({ nome, onFim }) => {
  const [saindo, setSaindo] = useState(false);
  const fechar = () => { if (saindo) return; setSaindo(true); setTimeout(onFim, 420); };

  useEffect(() => {
    const t = setTimeout(fechar, 2400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      onClick={fechar}
      style={{
        position: 'absolute', inset: 0, zIndex: 20, cursor: 'pointer',
        background: 'var(--paper)', display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 22,
        opacity: saindo ? 0 : 1, transition: 'opacity .42s ease',
      }}
    >
      <style>{`
        @keyframes fjBrasa {
          0%   { transform: scale(.35); opacity: 0 }
          45%  { transform: scale(1.12); opacity: .85 }
          100% { transform: scale(1); opacity: .5 }
        }
        @keyframes fjMarca {
          0%   { transform: scale(.82) translateY(6px); opacity: 0; filter: blur(6px) }
          100% { transform: none; opacity: 1; filter: none }
        }
        @keyframes fjTexto { 0% { opacity: 0; transform: translateY(9px) } 100% { opacity: 1; transform: none } }
        @keyframes fjRespira { 0%,100% { transform: scale(1); opacity: .5 } 50% { transform: scale(1.06); opacity: .72 } }
      `}</style>

      {/* a brasa acendendo atras da marca */}
      <div style={{ position: 'relative', width: 132, height: 132, display: 'grid', placeItems: 'center' }}>
        <div style={{
          position: 'absolute', inset: -18, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(232,122,59,.55), rgba(232,122,59,0) 68%)',
          animation: 'fjBrasa 1.1s cubic-bezier(.2,.8,.2,1) both, fjRespira 3.2s ease-in-out 1.1s infinite',
        }} />
        <img
          src="/icons/icon-192.png" alt="Forjamente" width={92} height={92}
          style={{
            width: 92, height: 92, borderRadius: 26, position: 'relative',
            boxShadow: '0 22px 50px -18px rgba(232,122,59,.65)',
            animation: 'fjMarca .72s cubic-bezier(.2,.9,.25,1) .18s both',
          }}
        />
      </div>

      <div style={{ textAlign: 'center', padding: '0 30px', animation: 'fjTexto .6s ease .62s both' }}>
        <div style={{ fontFamily: serif, fontSize: 30, lineHeight: 1.14, color: 'var(--ink)' }}>
          {nome ? <>Bem-vindo, <i>{nome}</i>.</> : <>Bem-vindo ao <i>Forjamente</i>.</>}
        </div>
        <div className="mono" style={{
          fontSize: 10.5, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: 'var(--ink-3)', marginTop: 14,
        }}>
          Ler · Lembrar · Evoluir
        </div>
      </div>
    </div>
  );
};

/* ── O fluxo ─────────────────────────────────────────────────────────── */
/**
 * Campo de texto livre no fim de um sheet de escolha.
 *
 * Fica DEPOIS dos atalhos de proposito: a curadoria tem valor (o gatilho com
 * maior efeito medido vem marcado), e oferecer o campo em branco primeiro
 * jogaria fora essa ajuda justo para quem mais precisa dela.
 */
const CampoLivre: React.FC<{
  valor: string;
  onChange: (v: string) => void;
  onConfirmar: (v: string) => void;
  placeholder: string;
  rotulo: string;
}> = ({ valor, onChange, onConfirmar, placeholder, rotulo }) => {
  const limpo = valor.trim();
  return (
    <div style={{
      marginTop: 10, paddingTop: 14, borderTop: '1px solid var(--line)',
    }}>
      <div className="mono" style={{
        fontSize: 9.5, letterSpacing: '0.14em', textTransform: 'uppercase',
        color: 'var(--ink-4)', fontWeight: 600, marginBottom: 9,
      }}>{rotulo}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          value={valor}
          onChange={(e) => onChange(e.target.value.slice(0, 60))}
          onKeyDown={(e) => { if (e.key === 'Enter' && limpo) onConfirmar(limpo); }}
          placeholder={placeholder}
          style={{
            flex: 1, padding: '13px 14px', borderRadius: 14, fontSize: 15,
            background: 'var(--paper-2)', border: '1px solid var(--line)',
            color: 'var(--ink)', outline: 'none', minWidth: 0,
          }}
        />
        <button
          onClick={() => limpo && onConfirmar(limpo)}
          disabled={!limpo}
          style={{
            padding: '13px 18px', borderRadius: 14, flexShrink: 0,
            border: '1px solid var(--ember)', background: 'transparent',
            color: 'var(--ember)', fontSize: 14, fontWeight: 600,
            opacity: limpo ? 1 : 0.35, cursor: limpo ? 'pointer' : 'default',
          }}>Usar</button>
      </div>
    </div>
  );
};

export const PreparoDoHabito: React.FC<{
  session: AuthSession | null;
  estado: EstadoHabito;
  // Quem já tem estante não pode ser obrigado a começar um livro novo.
  meusLivros?: BookV2[];
  onConcluir: (opts?: { lerAgora?: boolean; dispensou?: boolean }) => void;
  /** Primeiro nome de quem acabou de se cadastrar. Sem ele a tela abre
   *  argumentando com um desconhecido — e o app JA SABE quem e a pessoa. */
  nome?: string;
}> = ({ session, estado, meusLivros = [], nome, onConcluir }) => {
  const [sug, setSug] = useState<Sugestoes | null>(null);
  const [passo, setPasso] = useState<Passo>(() => {
    if (estado.estado === 'PRECISA_QUALIFICAR') return 'decisao';
    // 'interesse' era a tela que filtrava o catálogo, e ela morreu na W11 —
    // quando o livro passou a ser 100% do leitor. Este retorno sobreviveu à
    // remoção e apontava para um passo que não renderiza mais nada: quem
    // qualificasse, fechasse o app e voltasse caía numa TELA EM BRANCO, com a
    // barra de progresso e o botão do rodapé sozinhos.
    if (estado.falta?.livro) return 'livro';
    if (estado.falta?.contrato) return 'contrato';
    if (estado.falta?.ensaio) return 'ensaio';
    return 'decisao';
  });
  // So aparece para quem esta comecando de fato. Quem volta ao preparo no meio
  // (faltou o livro, faltou o ensaio) nao leva animacao de boas-vindas na cara.
  const [abertura, setAbertura] = useState(() => estado.estado === 'PRECISA_QUALIFICAR');
  const saudacao = (() => {
    const h = new Date().getHours();
    return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
  })();
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const [intencao, setIntencao] = useState<'forte' | 'explorando' | null>(null);
  const [perfil, setPerfil] = useState<'reencontro' | 'ignicao' | null>(estado.inscricao?.perfil || null);


  const [quando, setQuando] = useState(estado.contrato?.quando || '');
  const [quandoKind, setQuandoKind] = useState<'rotina' | 'horario'>(estado.contrato?.quandoKind || 'rotina');
  const [quandoHora, setQuandoHora] = useState(estado.contrato?.quandoHora?.slice(0, 5) || '21:00');
  const [onde, setOnde] = useState(estado.contrato?.onde || '');
  const [sheet, setSheet] = useState<null | 'quando' | 'onde'>(null);
  // Antessala do ensaio. Nao e passo da barra: o fluxo acabou de encolher
  // para 5, e o custo de um passo a mais em conclusao e alto. Aqui a
  // pessoa le a instrucao no ritmo dela e comeca quando quiser.
  const [ensaioIniciado, setEnsaioIniciado] = useState(false);
  // Texto digitado a mao nos sheets. A lista de atalhos cobre o comum; isto
  // cobre o resto da vida real (onibus, intervalo, depois de por o filho pra
  // dormir) — e o contrato so funciona se descrever a rotina de verdade.
  const [livreQuando, setLivreQuando] = useState('');
  const [livreOnde, setLivreOnde] = useState('');
  const [customizando, setCustomizando] = useState(false);

  useEffect(() => {
    habitoApi.sugestoes(session).then((s) => {
      setSug(s);
      // Correção 2: o gatilho vem PRÉ-SELECIONADO. Gatilho autogerado rende
      // d = 0,16; o tipo mais eficaz medido é "ao terminar uma etapa de tarefa".
      setQuando((q) => q || (s.gatilhos.find((g) => g.destaque) || s.gatilhos[0]).texto);
      setOnde((o) => o || s.lugares[0].texto);
    }).catch(() => {});
  }, [session]);

  // Uma tela a menos. A pergunta de interesse só servia para filtrar o catálogo
  // do app — sem catálogo, ela vira pergunta cuja resposta ninguém usa, e o
  // risco nomeado deste estágio é justamente o onboarding longo demais.
  // Quem escolheu "ja tentei" descreveu o proprio perfil na resposta — nao
  // faz sentido perguntar de novo no passo seguinte. O caminho encolhe.
  const pulaPublico = intencao === 'forte' && perfil !== null;
  // A tela de abertura SAIU do fluxo. Ela gastava um passo inteiro para
  // argumentar com quem ja tinha se cadastrado — ou seja, para vender a quem ja
  // disse sim. As boas-vindas viraram o cabecalho da primeira PERGUNTA, que e
  // onde o app comeca a montar algo para a pessoa em vez de falar sobre si.
  //
  // Sete passos tambem eram muitos: a taxa de conclusao de um fluxo de 7 e de
  // ~16%, contra ~72% num de 3. Aqui sao 5 (ou 4 para quem ja tentou antes).
  const caminho: Passo[] = intencao === 'explorando'
    ? ['decisao', 'convite']
    : pulaPublico
      ? ['decisao', 'livro', 'contrato', 'ensaio', 'selado']
      : ['decisao', 'publico', 'livro', 'contrato', 'ensaio', 'selado'];
  const idx = Math.max(0, caminho.indexOf(passo));

  // Voltar um passo. Só anda pra trás dentro do caminho já percorrido — o
  // estado que cada passo grava (qualificar/contrato/ensaio) e idempotente,
  // entao refazer um passo apenas regrava a mesma coisa.
  const podeVoltar = idx > 0 && passo !== 'selado';
  const voltarPasso = () => {
    if (!podeVoltar) return;
    setErro(null);
    // Se as cenas ja comecaram, voltar devolve a explicacao — nao joga a
    // pessoa para o contrato. Ela quase sempre quer reler a instrucao.
    if (passo === 'ensaio' && ensaioIniciado) { setEnsaioIniciado(false); return; }
    setPasso(caminho[idx - 1]);
  };

  async function qualificar(novaIntencao: 'forte' | 'explorando', novoPerfil?: 'reencontro' | 'ignicao', novoInteresse?: string) {
    setSalvando(true); setErro(null);
    try {
      await habitoApi.qualificar(session, {
        intencao: novaIntencao,
        perfil: novoPerfil || perfil || undefined,
        interesse: novoInteresse || undefined,
      });
    } catch (e: any) {
      setErro(e?.message || 'Não consegui salvar.');
      throw e;
    } finally { setSalvando(false); }
  }

  async function selarContrato() {
    setSalvando(true); setErro(null);
    try {
      await habitoApi.salvarContrato(session, {
        quando: quandoKind === 'horario' ? `às ${quandoHora}` : quando,
        quandoKind,
        quandoHora: quandoKind === 'horario' ? `${quandoHora}:00` : null,
        onde,
        sugerido: !customizando,
      });
      setPasso('ensaio');
    } catch (e: any) { setErro(e?.message || 'Não consegui salvar o contrato.'); }
    finally { setSalvando(false); }
  }

  async function concluirEnsaio() {
    setSalvando(true);
    try {
      await habitoApi.ensaiar(session);
      setPasso('selado');
    } catch (e: any) { setErro(e?.message || 'Não consegui selar.'); }
    finally { setSalvando(false); }
  }

  const quandoTexto = quandoKind === 'horario' ? `às ${quandoHora}` : quando;
  const paginaAlvo = (estado.livro?.proximaPagina) || 1;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9600, background: 'var(--paper)',
      display: 'flex', flexDirection: 'column', maxWidth: 460, margin: '0 auto',
      animation: 'e2FadeUp .28s ease both',
    }}>
      <style>{`
        @keyframes habRespira { 0%,100% { transform: scale(0.86); opacity:.22 } 50% { transform: scale(1.06); opacity:.5 } }
        @keyframes habSelo { 0% { transform: scale(1.5) rotate(-6deg); opacity: 0 } 60% { transform: scale(0.97) rotate(1deg); opacity: 1 } 100% { transform: scale(1) rotate(0); opacity: 1 } }
      `}</style>

      {abertura && <Abertura nome={nome} onFim={() => setAbertura(false)} />}

      {/* progresso + voltar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: 'calc(16px + env(safe-area-inset-top,0px)) 22px 6px',
      }}>
        {/* Ocupa o mesmo espaço mesmo invisível, pra barra não pular de lugar
            entre o primeiro passo e o segundo. */}
        <button
          onClick={voltarPasso}
          aria-label="Voltar"
          disabled={!podeVoltar}
          style={{
            width: 30, height: 30, borderRadius: 999, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid var(--line)', background: 'transparent',
            color: 'var(--ink-3)', cursor: podeVoltar ? 'pointer' : 'default',
            opacity: podeVoltar ? 1 : 0, pointerEvents: podeVoltar ? 'auto' : 'none',
            transition: 'opacity .25s',
          }}>
          <Icon name="chevron-left" size={15} color="var(--ink-2)" />
        </button>

        <div style={{ display: 'flex', gap: 5, flex: 1 }}>
          {caminho.map((_, k) => (
            <div key={k} style={{
              flex: 1, height: 3, borderRadius: 999,
              background: k <= idx ? 'var(--ember)' : 'var(--paper-3)', transition: 'background .35s',
            }} />
          ))}
        </div>
      </div>

      <div key={passo} style={{
        flex: 1, overflowY: 'auto', padding: '18px 24px 28px',
        display: 'flex', flexDirection: 'column',
        justifyContent: passo === 'livro' ? 'flex-start' : 'center',
        animation: 'e2FadeUp .32s ease both',
      }}>

        {/* ── Abertura: a promessa, sem número de dias ───────────────── */}
        {passo === 'decisao' && (<>
          {/* O acolhimento vive AQUI, junto da primeira pergunta — nao numa tela
              so dele. Chama pelo nome (o app sabe), diz o que a pessoa leva
              embora e quanto custa. A estatistica sobre leitura no pais saiu:
              ela e argumento de venda, e quem esta nesta tela ja comprou.
              A promessa concreta virou TITULO em vez de letra miuda cinza. */}
          <div className="mono" style={eyebrow}>{saudacao}{nome ? `, ${nome}` : ''}</div>
          <h2 style={titulo}>
            Em <i>um minuto e meio</i> você sai daqui com um livro e um plano de duas linhas.
          </h2>
          {/* O livro é o da pessoa (02/10/2026): "sai daqui com um livro", na
              primeira tela que todo cadastro vê, soava a entrega de conteúdo. */}
          <p data-clareza="seu-livro" style={{ ...corpo, marginTop: -2, marginBottom: 10, color: 'var(--ink-3)' }}>
            É o livro que você já tem em mãos — a gente não entrega conteúdo.
          </p>
          <p style={{ ...corpo, marginBottom: 22 }}>
            Só uma pergunta antes — para eu não te oferecer a coisa errada.
          </p>
          <div style={{ fontFamily: serif, fontSize: 21, color: 'var(--ink)', marginBottom: 12 }}>
            Onde você está agora?
          </div>
          {/* "Estou decidido" fala de INTENÇÃO, não de perfil. Antes ele
              gravava perfil 'ignicao' ("nunca peguei o gosto") — quem gosta de
              ler e está decidido era classificado como iniciante e recebia o
              programa errado. Agora só marca a intenção e o perfil é
              perguntado no passo seguinte, que é onde essa pergunta mora. */}
          <Escolha
            titulo="Estou decidido"
            legenda="quero ler todos os dias, e é isso que vim buscar aqui"
            selecionado={intencao === 'forte' && perfil === null}
            onClick={() => { setErro(null); setIntencao('forte'); setPerfil(null); setPasso('publico'); }}
          />
          {/* Esta resposta já descreve o perfil: quem tentou e não manteve tem
              relação com leitura, é reencontro. Por isso pula o passo seguinte
              em vez de perguntar a mesma coisa com outras palavras. */}
          <Escolha
            titulo="Quero muito, mas já tentei"
            legenda="comecei outras vezes e não durou"
            selecionado={intencao === 'forte' && perfil === 'reencontro'}
            onClick={async () => {
              setErro(null); setIntencao('forte'); setPerfil('reencontro');
              try { await qualificar('forte', 'reencontro'); setPasso('livro'); }
              catch (e: any) { setErro(e?.message || 'Não consegui salvar. Tente de novo.'); }
            }}
          />
          <Escolha
            titulo="Ainda estou olhando"
            legenda="não decidi nada, só vim conhecer o app"
            selecionado={intencao === 'explorando'}
            onClick={async () => {
              setErro(null); setIntencao('explorando');
              try { await qualificar('explorando'); setPasso('convite'); }
              catch (e: any) { setErro(e?.message || 'Não consegui salvar. Tente de novo.'); }
            }}
          />
          {/* A porta de saida continua existindo — o protocolo nao pode ser
              empurrado goela abaixo —, mas deixou de ser um link sublinhado
              competindo com o botao principal. Ela e rodape, nao alternativa. */}
          <button
            onClick={async () => {
              setSalvando(true);
              try { await habitoApi.dispensar(session); } catch {}
              onConcluir({ dispensou: true });
            }}
            style={{
              background: 'none', border: 'none', color: 'var(--ink-4)', fontSize: 12.5,
              padding: '18px 0 0', cursor: 'pointer', textAlign: 'left', alignSelf: 'flex-start',
            }}
          >
            Prefiro só usar o app por enquanto
          </button>
        </>)}

        {/* ── Estágio 0: a bifurcação de recompensa ──────────────────── */}
        {passo === 'publico' && (<>
          <div className="mono" style={eyebrow}>Sobre você e os livros</div>
          <h2 style={titulo}>E a leitura, <i>na sua vida</i>?</h2>
          <p style={{ ...corpo, marginBottom: 20 }}>
            Isso muda como o app vai te tratar daqui pra frente. Não é pegadinha.
          </p>
          <Escolha
            titulo="Eu gosto de ler"
            legenda="o problema não é gostar — é manter"
            selecionado={perfil === 'reencontro'}
            onClick={async () => {
              setErro(null); setPerfil('reencontro');
              try { await qualificar('forte', 'reencontro'); setPasso('livro'); }
              catch (e: any) { setErro(e?.message || 'Não consegui salvar. Tente de novo.'); }
            }}
          />
          <Escolha
            titulo="Nunca peguei o gosto"
            legenda="quero criar esse gosto do zero"
            selecionado={perfil === 'ignicao'}
            onClick={async () => {
              setErro(null); setPerfil('ignicao');
              try { await qualificar('forte', 'ignicao'); setPasso('livro'); }
              catch (e: any) { setErro(e?.message || 'Não consegui salvar. Tente de novo.'); }
            }}
          />
          <p style={{ fontSize: 12.5, color: 'var(--ink-4)', marginTop: 14, lineHeight: 1.5 }}>
            Para quem já gosta de ler, prêmio e pontuação atrapalham — corroem o
            prazer que já existe. Para quem está começando, ajudam. Um desenho só
            estaria errado para metade das pessoas.
          </p>
        </>)}

        {/* ── Estágio 1: o livro é do leitor ─────────────────────────── */}
        {passo === 'livro' && (<>
          <div className="mono" style={eyebrow}>Estágio 1 · a preparação</div>
          <h2 style={{ ...titulo, fontSize: 30 }}>Qual livro você<br />vai <i>ler</i>?</h2>
          <p style={{ ...corpo, marginBottom: 8 }}>
            O livro é seu — pegue o que já está aí. Fotografe a capa que eu leio o
            título e o autor.
          </p>
          <p style={{ ...corpo, fontSize: 13.5, color: 'var(--ink-3)', marginBottom: 18 }}>
            Um conselho, não uma regra: neste primeiro, o que importa é <i>terminar</i>.
            A chance de chegar ao fim depende muito mais do livro do que de você —
            uma história que prende é terminada por muito mais gente do que uma
            não-ficção densa.
            {/* Sem os percentuais (83% / 28%): número sem fonte na tela é a mesma
                regra que tirou números do e-mail de boas-vindas (pente fino, QA-12). */}
          </p>

          <EscolherLivro
            session={session}
            meusLivros={meusLivros}
            onPronto={() => setPasso('contrato')}
          />
        </>)}

        {/* ── Estágio 1: o contrato de DUAS variáveis ────────────────── */}
        {passo === 'contrato' && (<>
          <div className="mono" style={eyebrow}>Estágio 1 · o contrato</div>
          <h2 style={{ ...titulo, fontSize: 30 }}>Duas linhas. <i>Só isso.</i></h2>
          <p style={{ ...corpo, marginBottom: 22 }}>
            Não vou perguntar quantas páginas nem por quanto tempo. Quanto mais
            detalhe entra aqui, menos o plano funciona — e isso está medido.
          </p>

          <div style={{
            background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 22,
            padding: '22px 20px', fontFamily: serif, fontSize: 24, lineHeight: 1.5, color: 'var(--ink)',
          }}>
            {quandoKind === 'rotina' ? 'Assim que eu ' : 'Todo dia '}
            <button onClick={() => setSheet('quando')} style={{
              background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer',
              color: 'var(--ember)', borderBottom: '1.5px dashed var(--ember)',
            }}>
              {quandoKind === 'rotina' ? quando.replace(/^assim que eu /, '') : quandoTexto}
            </button>
            , eu leio{' '}
            <button onClick={() => setSheet('onde')} style={{
              background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer',
              color: 'var(--ember)', borderBottom: '1.5px dashed var(--ember)',
            }}>
              {onde}
            </button>
            .
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 9, marginTop: 16,
            padding: '12px 14px', background: 'var(--paper-2)', borderRadius: 14,
          }}>
            <Icon name="flame" size={16} color="var(--ember)" />
            <div style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.45 }}>
              Uma página vence o dia. Sempre. Esse é o acordo.
            </div>
          </div>

          <button onClick={() => { setQuandoKind(quandoKind === 'rotina' ? 'horario' : 'rotina'); setCustomizando(true); }} style={{
            background: 'none', border: 'none', color: 'var(--ember)', fontSize: 13.5,
            padding: '16px 0 0', cursor: 'pointer', textAlign: 'left',
            textDecoration: 'underline', textUnderlineOffset: 3, alignSelf: 'flex-start',
          }}>
            {quandoKind === 'rotina' ? 'Prefiro marcar um horário fixo' : 'Prefiro ancorar numa rotina'}
          </button>
          <p style={{ fontSize: 12, color: 'var(--ink-4)', marginTop: 6, lineHeight: 1.5 }}>
            Tanto faz qual dos dois — dois estudos compararam e não acharam diferença.
            Escolha o que for mais confiável na sua vida.
          </p>
        </>)}

        {/* ── Estágio 1: o ensaio ────────────────────────────────────── */}
        {passo === 'ensaio' && !ensaioIniciado && (<>
          <div className="mono" style={{ ...eyebrow, textAlign: 'center' }}>Antes de começar</div>
          <h2 style={{ ...titulo, fontSize: 29, textAlign: 'center', marginBottom: 14 }}>
            Agora é só <i>imaginar</i>.
          </h2>
          <p style={{ ...corpo, textAlign: 'center' }}>
            Vou te guiar por quatro cenas do seu plano — o momento, o lugar, o livro
            na mão. Leia cada uma e <b style={{ color: 'var(--ink)', fontWeight: 600 }}>veja
            a cena acontecendo</b>, como um filme curto.
          </p>
          <p style={{ ...corpo, textAlign: 'center', marginTop: 14, fontSize: 14.5, color: 'var(--ink-3)' }}>
            Leia a cena, feche os olhos e veja ela acontecer. Quando terminar, abra
            e toque em <b style={{ color: 'var(--ink-2)', fontWeight: 600 }}>próxima cena</b>.
            Sem pressa — o ritmo é seu.
          </p>

          <div style={{
            marginTop: 22, padding: '15px 17px', borderRadius: 16,
            background: 'var(--paper-2)', border: '1px solid var(--line)',
          }}>
            <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: 'var(--ink-2)', textAlign: 'center' }}>
              É uma vez só, e leva o tempo que você precisar. Quem ensaia o plano na cabeça tem
              um efeito bem maior do que quem só escreve. É a parte do preparo que mais pesa.
              {/* O "0,33 → 0,50" saiu da tela: número sem fonte citada ao leitor
                  (pente fino, QA-12). A referência segue no comentário do ensaio. */}
            </p>
          </div>

          <button
            onClick={() => setEnsaioIniciado(true)}
            style={{
              width: '100%', marginTop: 24, padding: '17px 20px', borderRadius: 999,
              border: 'none', background: 'var(--ember)', color: '#fff',
              fontSize: 16, fontWeight: 600, cursor: 'pointer',
            }}>Estou pronto</button>
        </>)}

        {passo === 'ensaio' && ensaioIniciado && (<>
          <div className="mono" style={{ ...eyebrow, textAlign: 'center' }}>O ensaio</div>
          <h2 style={{ ...titulo, fontSize: 27, textAlign: 'center', marginBottom: 4 }}>Uma vez só.</h2>
          <p style={{ ...corpo, textAlign: 'center', fontSize: 14, marginBottom: 18 }}>
            Feche os olhos em cada uma. Toque quando estiver pronto para a próxima.
          </p>
          <Ensaio
            momento={quandoKind === 'horario'
              ? `São ${quandoHora}.`
              : `Você acabou de ${quando.replace(/^assim que eu /, '')}.`}
            onde={onde}
            pagina={paginaAlvo}
            onPronto={concluirEnsaio}
          />
        </>)}

        {/* ── Selado ─────────────────────────────────────────────────── */}
        {passo === 'selado' && (<>
          <div style={{ textAlign: 'center', animation: 'habSelo .6s cubic-bezier(.2,.8,.2,1) both' }}>
            <div style={{
              width: 62, height: 62, borderRadius: '50%', margin: '0 auto 18px',
              background: 'var(--ember)', display: 'grid', placeItems: 'center',
              boxShadow: '0 12px 30px -12px var(--ember)',
            }}>
              <Icon name="check" size={28} color="#fff" />
            </div>
            <div className="mono" style={{ ...eyebrow, textAlign: 'center' }}>Está selado</div>
            <h2 style={{ ...titulo, fontSize: 29, textAlign: 'center' }}>
              Seu dia 1 começa quando<br />você <i>abrir o livro</i>.
            </h2>
          </div>

          <div style={{
            background: 'var(--paper-2)', border: '1px solid var(--line)', borderRadius: 22,
            padding: 20, marginTop: 8,
          }}>
            <div className="mono" style={{ ...eyebrow, marginBottom: 8 }}>Seu contrato</div>
            <div style={{ fontFamily: serif, fontSize: 21, lineHeight: 1.45, color: 'var(--ink)' }}>
              {quandoKind === 'rotina' ? 'Assim que eu ' : 'Todo dia '}
              <span style={{ color: 'var(--ember)' }}>{quandoKind === 'rotina' ? quando.replace(/^assim que eu /, '') : quandoTexto}</span>
              , eu leio <span style={{ color: 'var(--ember)' }}>{onde}</span>.
            </div>
          </div>

          <p style={{ ...corpo, fontSize: 13.5, marginTop: 16, color: 'var(--ink-3)' }}>
            Sessenta e sete dias é a mediana dos estudos, não uma promessa. O que eu
            posso prometer é que o programa continua com você depois disso.
          </p>
        </>)}

        {/* ── Caminho de quem ainda não decidiu ──────────────────────── */}
        {passo === 'convite' && (<>
          <div className="mono" style={eyebrow}>Sem programa, sem contrato</div>
          <h2 style={titulo}>Então vamos<br /><i>sem compromisso</i>.</h2>
          <p style={corpo}>
            Nada de sequência, nada de meta, nada cobrando você. O app fica seu para
            explorar: adicione os livros que você já tem, registre o que ler e veja
            como isso se acumula.
          </p>
          <p style={{ ...corpo, marginTop: 14 }}>
            Quando bater a vontade de transformar isso em hábito, toque em{' '}
            <b style={{ color: 'var(--ember)', fontWeight: 600 }}>Protocolo</b> lá no
            topo da tela inicial. Ele começa quando você decidir — não antes.
          </p>
          <p style={{ ...corpo, marginTop: 14, color: 'var(--ink-3)', fontSize: 14.5 }}>
            Não vou te perguntar de novo.
          </p>
        </>)}

        {erro && <div style={{ fontSize: 13, color: '#E2574C', marginTop: 14 }}>{erro}</div>}
      </div>

      {/* rodapé */}
      {['contrato', 'selado', 'convite'].includes(passo) && (
        <div style={{ padding: '10px 24px calc(20px + env(safe-area-inset-bottom,0px))' }}>
          <button
            onClick={() => {
              if (passo === 'contrato') selarContrato();
              else if (passo === 'selado') onConcluir({ lerAgora: true });
              else if (passo === 'convite') onConcluir();
            }}
            disabled={salvando}
            style={{
              width: '100%', padding: '17px 20px', borderRadius: 999, border: 'none',
              background: 'var(--ember)',
              color: '#fff',
              fontSize: 16, fontWeight: 600, cursor: 'pointer', transition: 'background .25s',
            }}
          >
            {salvando ? 'um instante…'
              : passo === 'contrato' ? 'Ensaiar meu plano'
              : passo === 'selado' ? 'Pegar o livro em mãos'
              : 'Explorar o app'}
          </button>
          {passo === 'selado' && (
            <button onClick={() => onConcluir()} style={{
              width: '100%', background: 'none', border: 'none', color: 'var(--ink-3)',
              fontSize: 14, padding: '13px 0 0', cursor: 'pointer',
            }}>
              Depois, na minha hora
            </button>
          )}
        </div>
      )}

      {/* escolha do gatilho — recomendações fortes, não lista neutra */}
      <Sheet open={sheet === 'quando'} onClose={() => setSheet(null)} title="Quando">
        <div style={{ paddingBottom: 12 }}>
          {quandoKind === 'horario' ? (
            <div style={{ padding: '4px 0 12px' }}>
              <input
                type="time" value={quandoHora}
                onChange={(e) => { setQuandoHora(e.target.value); setCustomizando(true); }}
                style={{
                  width: '100%', padding: '14px 16px', borderRadius: 14, fontSize: 20,
                  background: 'var(--paper-2)', border: '1px solid var(--line)', color: 'var(--ink)',
                  fontFamily: 'Geist Mono, monospace',
                }}
              />
            </div>
          ) : (sug?.gatilhos || []).map((g) => (
            <Escolha
              key={g.id}
              titulo={g.texto.replace(/^assim que eu /, 'Assim que eu ')}
              legenda={g.destaque ? 'o tipo de gatilho com maior efeito medido' : undefined}
              selecionado={quando === g.texto}
              onClick={() => { setQuando(g.texto); if (!g.destaque) setCustomizando(true); setSheet(null); }}
            />
          ))}

          {quandoKind !== 'horario' && (
            <CampoLivre
              rotulo="ou escreva o seu"
              placeholder="ex.: descer do ônibus"
              valor={livreQuando}
              onChange={setLivreQuando}
              onConfirmar={(v) => {
                // Mesmo formato dos gatilhos da lista: o contrato monta a frase
                // com "Assim que eu ..." e remove esse prefixo pra exibir.
                setQuando('assim que eu ' + v.replace(/^assim que eu /i, ''));
                setCustomizando(true);
                setLivreQuando('');
                setSheet(null);
              }}
            />
          )}
        </div>
      </Sheet>

      <Sheet open={sheet === 'onde'} onClose={() => setSheet(null)} title="Onde">
        <div style={{ paddingBottom: 12 }}>
          {(sug?.lugares || []).map((l) => (
            <Escolha
              key={l.id}
              titulo={l.texto}
              selecionado={onde === l.texto}
              onClick={() => { setOnde(l.texto); setSheet(null); }}
            />
          ))}

          <CampoLivre
            rotulo="ou escreva o seu"
            placeholder="ex.: na varanda de casa"
            valor={livreOnde}
            onChange={setLivreOnde}
            onConfirmar={(v) => { setOnde(v); setLivreOnde(''); setSheet(null); }}
          />
        </div>
      </Sheet>
    </div>
  );
};

export default PreparoDoHabito;
